// Action Creators
// Constants
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { LogicModule } from "@ms/planner-logicmodules/lib/local/modules/LogicModule";
// Performance
import { namesNoWork, TrackedInteractions } from "@ms/planner-stores/lib/local/performance/InteractionRegistry";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Utilities
import { checkIfNonNativeViewFromSubRouteType, extractPlanRoutingDetailsFromSubRoute, extractPrimaryViewIdFromRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
/**
 * ActionCreator for performance data collection like EUPL and PLT, and the interaction tracking
 * lifecycle: start records what was acted on, complete signals a view, cancel abandons what is left.
 */ export class PerformanceCollectionActionCreator {
    appStart(pageName, appStartTime) {}
    pageReady(dataFetchStart, dataFetchEnd, exportName, metaTag, extraData) {
        this.logViewRenderPerfMarkerForUnoNonNativeViews();
    }
    /**
     * Begin tracking a user interaction. A newer interaction of the same name on the same view
     * supersedes the one in flight, since only the latest can still match what the view ends up holding.
     * @param interactionId Which interaction this is and what it was performed on
     * @param targetViewId The view whose response completes this interaction
     * @param entryPoint Where the user triggered it from
     * @param extraData Optional further context logged with the measurement
     */ startInteraction(interactionId, targetViewId, entryPoint, extraData) {
        if (!this.configProvider().settings.performanceSettings.enableLatencyTracking) {
            return;
        }
        const startTime = performance.now();
        // Nothing was asked for, so there is nothing to measure and nothing to prove it finished.
        if (namesNoWork(interactionId.key)) {
            return;
        }
        // Already true before anything was dispatched, so no repaint is owed and only unrelated work
        // later could settle it.
        if (TrackedInteractions[interactionId.name].isComplete(interactionId, this.storeLoader, targetViewId, null)) {
            return;
        }
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        const module = this.modules.load(LogicModule.PerformanceCollection);
        // The older one is closed out with its own start time, so its measurement reflects the wait the
        // user actually had.
        const superseded = perfStore.getPendingInteraction(targetViewId, interactionId.name);
        if (superseded != null) {
            module.abandonTrackedInteraction(superseded);
        }
        module.startTrackedInteraction(interactionId, startTime, targetViewId, entryPoint, extraData);
    }
    /**
     * Records how far a view has rendered, completing anything that render finished. The state is
     * handed over rather than read back, since the write below is dispatched asynchronously.
     */ viewRender(viewDetails, state, extraData) {
        this.completeInteractions(viewDetails, state);
        this.modules.load(LogicModule.PerformanceCollection).setViewRenderState(viewDetails, state, extraData);
    }
    /**
     * Signal that something happened on a view, completing everything it finished. Callers report what
     * they did, not what it finished, so signalling too early costs nothing.
     * @param targetViewId The view something happened on
     * @param renderState How far that view has rendered, handed over by the render signal. Null when
     * what happened was not a render, such as a repaint.
     */ completeInteractions(targetViewId, renderState) {
        if (!this.configProvider().settings.performanceSettings.enableLatencyTracking) {
            return;
        }
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        const module = this.modules.load(LogicModule.PerformanceCollection);
        for (const pending of perfStore.getPendingInteractionsByViewId(targetViewId)){
            // The action layer sits below the registry, so the store keeps name and key apart. They were
            // written together by `startInteraction`, so recomposing them here is always the declared pair.
            const interactionId = {
                name: pending.name,
                key: pending.uniqueKey
            };
            if (TrackedInteractions[pending.name].isComplete(interactionId, this.storeLoader, pending.targetViewId, renderState)) {
                module.settleTrackedInteraction(targetViewId, pending);
            }
        }
    }
    /**
     * Cancel tracking for a view, logging anything still pending as abandoned.
     *
     * Flight-gated like the rest: resetting render state is a write `BootObserverWorker` reads to
     * gate full boot, so with tracking off this must not run at all.
     */ cancelInteractions(targetViewId) {
        if (!this.configProvider().settings.performanceSettings.enableLatencyTracking) {
            return;
        }
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        const module = this.modules.load(LogicModule.PerformanceCollection);
        module.cancelTrackedInteractions(targetViewId, perfStore.getPendingInteractionsByViewId(targetViewId));
    }
    /** Logs view render perf marker for UNO non-native views on page ready. */ logViewRenderPerfMarkerForUnoNonNativeViews() {
        const userId = this.storeLoader.load(Store.AppContext).getAppContext().userId ?? "";
        const routeStore = this.storeLoader.load(Store.Route);
        const currentSubRouteType = routeStore.getCurrentSubRouteType();
        const currentSubRoute = routeStore.getCurrentSubRoute();
        // Don't log ViewRender marker on pageReady for native views
        if (!checkIfNonNativeViewFromSubRouteType(currentSubRouteType, this.configProvider)) {
            return;
        }
        this.viewRender(extractPrimaryViewIdFromRoute(currentSubRouteType, currentSubRoute, userId), ViewRenderState.Critical, {
            viewType: `${extractPlanRoutingDetailsFromSubRoute(currentSubRoute, currentSubRouteType)?.viewType}`
        });
    }
    /**
     * Initializes a new instance of the performance collector action creator
     * @param modules The logic module providers the SPA uses to get logic modules
     * @param storeLoader Store loader for reading store state
     * @param configProvider Configuration provider for feature flags
     */ constructor(modules, storeLoader, configProvider){
        this.modules = modules;
        this.storeLoader = storeLoader;
        this.configProvider = configProvider;
    }
}
